import type { ComponentType } from 'react';
import Svg, { Circle, Line, Path, Rect } from 'react-native-svg';

/**
 * The FlowSellAI "Command Deck" icon set — ported 1:1 from the Claude Design
 * prototype (ui_kits/app/icons.jsx) so the sidebar uses the exact same glyphs as
 * the design instead of the nearest lucide approximation.
 */
export interface CommandDeckIconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

export type CommandDeckIcon = ComponentType<CommandDeckIconProps>;

function svgProps(size: number, color: string, strokeWidth: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
}

export function GridIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />
    </Svg>
  );
}

export function PipelineIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M5 10v8M12 4v14M19 13v5" />
    </Svg>
  );
}

export function UsersIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <Circle cx={9} cy={7} r={4} />
      <Path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  );
}

export function ChartIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Line x1={18} y1={20} x2={18} y2={10} />
      <Line x1={12} y1={20} x2={12} y2={4} />
      <Line x1={6} y1={20} x2={6} y2={14} />
    </Svg>
  );
}

export function ChatIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.6-.8L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
    </Svg>
  );
}

export function BotIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Rect x={3} y={11} width={18} height={10} rx={2} />
      <Circle cx={12} cy={6} r={2} />
      <Path d="M12 8v3" />
      <Line x1={8} y1={16} x2={8} y2={16} />
      <Line x1={16} y1={16} x2={16} y2={16} />
    </Svg>
  );
}

export function WorkflowsIcon({
  size = 18,
  color = '#fff',
  strokeWidth = 2,
}: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Rect x={3} y={3} width={7} height={7} rx={1.5} />
      <Rect x={14} y={14} width={7} height={7} rx={1.5} />
      <Path d="M6.5 10v3a2 2 0 0 0 2 2H14" />
    </Svg>
  );
}

export function PhoneIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </Svg>
  );
}

export function SettingsIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Circle cx={12} cy={12} r={3} />
      <Path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 14H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.6l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 9H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </Svg>
  );
}

export function LogoutIcon({ size = 18, color = '#fff', strokeWidth = 2 }: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <Path d="M16 17l5-5-5-5M21 12H9" />
    </Svg>
  );
}

export function SparkleBurstIcon({
  size = 18,
  color = '#fff',
  strokeWidth = 2,
}: CommandDeckIconProps) {
  return (
    <Svg {...svgProps(size, color, strokeWidth)}>
      <Path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" />
    </Svg>
  );
}
